import { FC, ReactElement } from "react";
import {
  MenuItem,
  FormControl,
  Select,
  Box,
  Typography,
  styled,
  FormHelperText,
  ListItemIcon,
  SelectProps,
  SelectChangeEvent,
} from "@mui/material";
import { ErrorOutline, Check } from "@mui/icons-material";

interface Label {
  value: string;
  label: string;
  color?: string;
  disabled?: boolean;
  imgUrl?: string;
}

type ISelectProps = {
  value: string;
  options: Label[];
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  handleChangeValue: (value: string, label?: string) => void;
  customeMenuStyles?: React.CSSProperties;
} & SelectProps;

const MuiSelect = styled(Select)(() => ({
  background: "#FFF",
  border: "1px solid #E6E7E7",
  boxShadow: "none",
  borderRadius: "10px",
  maxHeight: 48,
  "& .MuiSelect-select": {
    padding: "12px 1rem",
  },
  "& .MuiSelect-select .MuiListItemIcon-root": {
    display: "none",
  },
  "& fieldset": {
    border: "none",
  },
}));

const SelectBox: FC<ISelectProps> = ({
  value,
  options,
  label,
  required,
  error,
  helperText,
  handleChangeValue,
  customeMenuStyles,
  ...rest
}): ReactElement => {
  const handleChange = (
    event: SelectChangeEvent<unknown>,
    child?: ReactNode
  ) => {
    handleChangeValue(
      event.target.value as string,
      child.props.children as string
    );
  };

  const menuStyle = {
    maxHeight: 500,
    marginTop: "10px",
    padding: "5px",
  };

  return (
    <>
      {label && (
        <Typography
          sx={{
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            color: "#030712",
          }}
          variant="subtitle2"
        >
          {label} {required && <Typography color={"error"}>*</Typography>}
        </Typography>
      )}
      <FormControl fullWidth error={error}>
        <MuiSelect
          {...rest}
          value={value}
          onChange={handleChange}
          placeholder="Please Select"
          MenuProps={{
            PaperProps: {
              style: customeMenuStyles
                ? { ...customeMenuStyles, ...menuStyle }
                : menuStyle,
            },
          }}
          displayEmpty
          renderValue={
            value !== ""
              ? undefined
              : () => (
                  <span
                    style={{ color: "#8E9494", fontWeight: 400, fontSize: 16 }}
                  >
                    {rest?.placeholder}
                  </span>
                )
          }
        >
          {options?.map((option: Label): JSX.Element => {
            return (
              <MenuItem
                value={option.value}
                key={option.value}
                disabled={option.disabled}
                sx={(theme) => ({
                  display: "flex",
                  justifyContent: "space-between",
                  "&:hover": {
                    backgroundColor: "rgba(28, 180, 186, 0.15)",
                    color: theme.palette.primary.main,
                    borderRadius: "4px",
                  },
                })}
              >
                <Box display={"flex"} alignItems={"center"} gap={"8px"}>
                  {option.imgUrl && (
                    <Box
                      component="img"
                      src={option.imgUrl}
                      width={24}
                      height={24}
                      borderRadius="50%"
                    />
                  )}
                  {option.color && (
                    <Box
                      width={11}
                      height={11}
                      sx={{
                        background: option.color,
                      }}
                      borderRadius={"2px !important"}
                    />
                  )}
                  {option.label}
                </Box>
                {value === option.value && (
                  <ListItemIcon>
                    <Check />
                  </ListItemIcon>
                )}
              </MenuItem>
            );
          })}
        </MuiSelect>
        {error && helperText !== "" && (
          <FormHelperText
            sx={{
              display: "flex",
              alignItems: "center",
              margin: "0",
              py: "5px",
              gap: "3px",
            }}
          >
            <ErrorOutline sx={{ fontSize: "15px" }} />
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    </>
  );
};

export default SelectBox;
